import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ExtendedListItem from "../../components/ExtendedListItem/ExtendedListItem";
import PrivilegeDialog from "./PrivilegeDialog/PrivilegeDialog";
import PrivilegeItem from "./PrivilegeItem/PrivilegeItem";
import Searchbar from "../../components/Searchbar/Searchbar";
import UppercaseButton from "../../components/UppercaseButton/UppercaseButton";
import Paper from "../../components/Paper/Paper";
import {
  PRIVILEGES_ENDPOINT,
  postRequest,
  getRequest,
  PRIVILEGES_OVERVIEW_ENDPOINT,
  putRequest,
  deleteRequest,
} from "../../ApiService";
import { Privilege } from "./Models/Privilige";
import {
  ERROR_MESSAGE,
  ERROR_TITLE,
  PRIVILEGE_CREATE,
  PRIVILEGE_CREATE_ERROR,
  PRIVILEGE_SEARCH,
  PRIVILEGE_UPDATE_ERROR,
} from "../../utility/TextLabels";
import { PrivilegeUpdate } from "./Models/PrivilegeUpdate";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";

const PrivilegesPage = () => {
  const [isPrivilegeDialogOpen, setIsPrivilegeDialogOpen] = useState(false);
  const [privileges, setPrivileges] = useState<Privilege[]>([]);
  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");

  useEffect(() => {
    getPrivilegeOverview();
  }, []);

  const createPrivilege = async (privilege: Privilege) => {
    try {
      await postRequest(PRIVILEGES_ENDPOINT, privilege);
    } catch (error) {
      setErrorMessageText(PRIVILEGE_CREATE_ERROR);
      setIsErrorMessageOpen(true);
    }
  };

  const updatePrivilege = async (privilege: Privilege) => {
    const privilegeUpdate: PrivilegeUpdate = {
      name: privilege.name,
      description: privilege.description ?? "",
    };
    try {
      await putRequest(PRIVILEGES_ENDPOINT, privilegeUpdate, privilege.id!);
    } catch (error) {
      setErrorMessageText(PRIVILEGE_UPDATE_ERROR);
      setIsErrorMessageOpen(true);
    }
    await getPrivilegeOverview();
  };

  const deletePrivilege = async (privilege: Privilege) => {
    try {
      await deleteRequest(PRIVILEGES_ENDPOINT, privilege.id!);
    } catch (error) {
      setErrorMessageText(ERROR_MESSAGE);
      setIsErrorMessageOpen(true);
    }
    await getPrivilegeOverview();
  };

  const getPrivilegeOverview = async (searchValue?: string) => {
    try {
      await getRequest(PRIVILEGES_OVERVIEW_ENDPOINT, searchValue ?? "").then(
        (fetchedPrivileges: Privilege[]) => {
          setPrivileges(fetchedPrivileges);
        }
      );
    } catch (error) {
      setErrorMessageText(ERROR_MESSAGE);
      setIsErrorMessageOpen(true);
    }
  };

  const createPrivilegeAndUpdateOverview = async (privilege: Privilege) => {
    await createPrivilege(privilege);
    await getPrivilegeOverview();
  };

  return (
    <>
      <ErrorMessage
        isOpen={isErrorMessageOpen}
        primaryText={ERROR_TITLE}
        additionalText={errorMessageText}
        onClose={() => setIsErrorMessageOpen(false)}
      ></ErrorMessage>

      <Paper>
        <Box display="flex" justifyContent="space-between">
          <Searchbar
            placeholder={PRIVILEGE_SEARCH}
            onChange={getPrivilegeOverview}
          />
          <UppercaseButton
            color="primary"
            onClick={() => setIsPrivilegeDialogOpen(true)}
          >
            {PRIVILEGE_CREATE}
          </UppercaseButton>
          <PrivilegeDialog
            title={PRIVILEGE_CREATE}
            isOpen={isPrivilegeDialogOpen}
            onClose={() => setIsPrivilegeDialogOpen(false)}
            onSubmit={createPrivilegeAndUpdateOverview}
          />
        </Box>
        <List>
          {privileges.map((privilege, index) => (
            <ExtendedListItem
              key={index}
              start={index === 0}
              end={index === privileges.length - 1}
            >
              <PrivilegeItem
                privilege={privilege}
                onUpdatePrivilege={updatePrivilege}
                onDeletePrivilege={deletePrivilege}
              />
            </ExtendedListItem>
          ))}
        </List>
      </Paper>
    </>
  );
};

export default PrivilegesPage;
